import { useRef, useState } from 'react'
import { GripVertical, Pencil, Trash2, Loader2, FileEdit } from 'lucide-react'
import { Button as HeroButton, Tooltip } from '@heroui/react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { FunnelBadge } from '../shared/FunnelBadge'
import { OfferPreview } from './OfferPreview'
import type { FunnelStep } from '../../types'

interface StepCardProps {
  step: FunnelStep
  onEdit: (stepId: string) => void
  onDelete: (stepId: string) => void
  onBuildPage?: (stepId: string) => void
  isSaving?: boolean
}

export function StepCard({ step, onEdit, onDelete, onBuildPage, isSaving = false }: StepCardProps) {
  const [removing, setRemoving] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    ...(isDragging ? {} : { borderColor: 'var(--fn-border)', backgroundColor: 'var(--fn-bg-card)' }),
  }

  const handleDelete = () => {
    if (!window.confirm(`Delete step "${step.name || `Step ${step.position + 1}`}"? This cannot be undone.`)) return
    setRemoving(true)
    const el = cardRef.current
    if (el) {
      el.addEventListener('animationend', () => onDelete(step.id), { once: true })
    } else {
      onDelete(step.id)
    }
  }

  return (
    <div
      ref={(node) => {
        setNodeRef(node)
        ;(cardRef as React.MutableRefObject<HTMLDivElement | null>).current = node
      }}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border ${ removing ? 'animate-fade-slide-out' : 'animate-fade-slide-in' }`}
      {...attributes}
      role="option"
      aria-label={`Step ${step.position + 1}: ${step.name || `Step ${step.position + 1}`}, position ${step.position + 1}`}
      aria-busy={isSaving}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing"
        {...listeners}
        aria-label={`Drag to reorder step ${step.position + 1}`}
        disabled={isSaving}
      >
        {isSaving ? (
          <Loader2 className="w-4 h-4 animate-spin text-fn-text-muted" />
        ) : (
          <GripVertical className="w-4 h-4 text-fn-text-muted" />
        )}
      </button>

      <div
        className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium text-fn-primary"
        style={{
          backgroundColor: 'var(--fn-primary-light)',
        }}
      >
        {step.position + 1}
      </div>

      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-fn-text">
            {step.name || `Step ${step.position + 1}`}
          </span>
          <FunnelBadge type="step" value={step.stepType} />
        </div>
        {step.offerId ? (
          <OfferPreview offerId={step.offerId} />
        ) : (
          <p className="text-xs mt-0.5 text-fn-text-muted">
            No offer assigned
          </p>
        )}
      </div>

      <div className="flex items-center gap-1">
        <Tooltip content="Edit step">
          <HeroButton
            isIconOnly
            variant="light"
            size="sm"
            onPress={() => onEdit(step.id)}
            aria-label="Edit step"
          >
            <Pencil className="w-4 h-4" />
          </HeroButton>
        </Tooltip>

        {onBuildPage && (
          <Tooltip content="Build page">
            <HeroButton
              isIconOnly
              variant="light"
              size="sm"
              onPress={() => onBuildPage(step.id)}
              aria-label="Build page"
            >
              <FileEdit className="w-4 h-4" />
            </HeroButton>
          </Tooltip>
        )}

        <Tooltip content="Delete step">
          <HeroButton
            isIconOnly
            variant="light"
            size="sm"
            color="danger"
            onPress={handleDelete}
            aria-label="Delete step"
          >
            <Trash2 className="w-4 h-4" />
          </HeroButton>
        </Tooltip>
      </div>
    </div>
  )
}
